// Inline (pay-first) store checkout payment. Shopify-style: the card field
// lives on the checkout page itself and a single "Pay now" button charges it.
// Flow: prepare (server quotes + creates PaymentIntent, stages the cart) →
// confirmCardPayment → finalize (server verifies the charge, THEN creates the
// order/invoice/rentals). Closing the page mid-way creates nothing — and if
// the card was charged but the browser died, the Stripe webhook finalizes.
import { useCallback, useEffect, useRef, useState } from 'react';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { CreditCard, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import {
  prepareStoreCheckout,
  finalizeStoreCheckout,
  storeCheckoutStatus,
  submitStoreCheckout,
  type StoreCheckoutPayload,
  type StoreCheckoutResult,
} from '../../lib/storefront';
import type { RentalCheckoutTerm } from './RentalCartNotice';

interface InlineCheckoutPaymentProps {
  businessId: string;
  businessName: string;
  accent: string;
  /** Checkout form incomplete — button disabled (the form shows why). */
  disabled: boolean;
  /** Snapshot of the checkout payload at pay time. */
  buildPayload: () => StoreCheckoutPayload;
  /** 'full' = whole cart charged now; 'rental_split' = only the rental first period. */
  chargeKind: 'full' | 'rental_split';
  /** Display estimate for the button label (server recomputes authoritatively). */
  estimatedChargeCents: number;
  surcharge: { enabled: boolean; percent: number };
  /** Rental lines in the cart — a card is always captured, and terms must be accepted on the page. */
  rentals: RentalCheckoutTerm[];
  /** Customer checked the agreement box in the rental cart notice. Ignored when there are no rentals. */
  termsAccepted?: boolean;
  rentalFirstPeriodLabel: string;
  onComplete: (result: StoreCheckoutResult) => void;
}

const money = (cents: number) => `$${(cents / 100).toFixed(2)}`;

export default function InlineCheckoutPayment(props: InlineCheckoutPaymentProps) {
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const platformKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
        const { data, error } = await supabase.functions.invoke('get-stripe-connect-publishable-key', {
          body: { businessId: props.businessId, platformPublishableKey: platformKey },
        });
        if (error || !data?.publishableKey || !data?.accountId) {
          throw new Error('Unable to initialize secure payment');
        }
        if (!cancelled) {
          setStripePromise(loadStripe(data.publishableKey, { stripeAccount: data.accountId }));
        }
      } catch {
        if (!cancelled) setInitError('Unable to initialize secure payment — please refresh and try again.');
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.businessId]);

  if (initError) {
    return <p className="text-[13px] text-red-600">{initError}</p>;
  }
  if (!stripePromise) {
    return (
      <button disabled className="w-full py-4 rounded-lg text-base font-semibold bg-gray-200 text-gray-400 cursor-not-allowed">
        Loading secure payment…
      </button>
    );
  }
  return (
    <Elements stripe={stripePromise}>
      <InlinePayForm {...props} />
    </Elements>
  );
}

function InlinePayForm({
  businessName, accent, disabled, buildPayload, chargeKind,
  estimatedChargeCents, surcharge, rentals, termsAccepted = false, onComplete,
}: InlineCheckoutPaymentProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardFunding, setCardFunding] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<'idle' | 'preparing' | 'charging' | 'finalizing'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [paidPending, setPaidPending] = useState(false);
  // Server said the wallet doesn't cover the order after all (balance changed
  // since the page loaded) — reveal the card form and ask for a card.
  const [cardRequiredOverride, setCardRequiredOverride] = useState(false);
  const fundingReqRef = useRef(0);

  // Wallet credit covers the whole charge (estimate — server re-checks at
  // prepare) and nothing needs a card on file → no card entry at all.
  // Rentals ALWAYS capture a card: they bill automatically each period.
  const walletCoversAll = estimatedChargeCents <= 0 && rentals.length === 0;
  const needCardEntry = !walletCoversAll || cardRequiredOverride;

  // Detect the card's funding type once it's fully entered, so a credit-card
  // surcharge can be shown BEFORE the customer commits (debit pays none).
  useEffect(() => {
    if (!cardComplete || !surcharge.enabled || surcharge.percent <= 0 || !stripe || !elements) {
      setCardFunding(null);
      return;
    }
    const reqId = ++fundingReqRef.current;
    const card = elements.getElement(CardElement);
    if (!card) return;
    stripe.createPaymentMethod({ type: 'card', card }).then(({ paymentMethod }) => {
      if (fundingReqRef.current !== reqId) return;
      setCardFunding(paymentMethod?.card?.funding || null);
    }).catch(() => { /* fee re-checked server-side at prepare */ });
  }, [cardComplete, surcharge.enabled, surcharge.percent, stripe, elements]);

  const surchargeApplies = surcharge.enabled && surcharge.percent > 0 && cardFunding === 'credit';
  const estimatedSurchargeCents = surchargeApplies
    ? Math.round(estimatedChargeCents * (surcharge.percent / 100))
    : 0;
  const buttonTotalCents = estimatedChargeCents + estimatedSurchargeCents;

  /** Poll the intent until the webhook (or a parallel finalize) completes it. */
  const pollUntilComplete = useCallback(async (checkoutIntentId: string): Promise<StoreCheckoutResult | null> => {
    for (let i = 0; i < 20; i++) {
      await new Promise(r => setTimeout(r, 3000));
      try {
        const s = await storeCheckoutStatus(checkoutIntentId);
        if (s.status === 'completed' && s.result) return s.result;
      } catch { /* keep polling */ }
    }
    return null;
  }, []);

  const pay = useCallback(async () => {
    if (!stripe || !elements || busy) return;
    setBusy(true);
    setError(null);
    try {
      // 1. Stage the checkout server-side (nothing visible created yet).
      setStage('preparing');
      const payload = buildPayload();
      const prep = await prepareStoreCheckout({ ...payload, cardFunding });
      if (prep.mode === 'no_charge') {
        // Settings changed under us — no card needed after all.
        const result = await submitStoreCheckout(payload);
        onComplete(result);
        return;
      }
      if (!prep.checkoutIntentId) throw new Error('Could not start checkout — please try again');

      // 2. Charge the card (skipped when wallet credit covers everything).
      if ((prep.chargeCents ?? 0) > 0) {
        // The card form is hidden when the wallet was expected to cover the
        // order — if the server disagrees (balance changed), ask for a card.
        const card = elements.getElement(CardElement);
        if (!card) {
          setCardRequiredOverride(true);
          throw new Error(
            `Your wallet balance no longer covers this order — please enter a card to pay the remaining ${money(
              (prep.chargeCents ?? 0) + (prep.surchargeCents ?? 0),
            )}.`,
          );
        }
        if (!prep.clientSecret) throw new Error('Could not start the payment — please try again');
        setStage('charging');
        const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(prep.clientSecret, {
          payment_method: { card },
        });
        if (confirmError) throw new Error(confirmError.message || 'Your card was declined');
        if (paymentIntent?.status !== 'succeeded') throw new Error('Payment did not complete — please try again');
      }

      // 3. Create the order now that the money moved.
      setStage('finalizing');
      let result: StoreCheckoutResult | null = null;
      try {
        const fin = await finalizeStoreCheckout(prep.checkoutIntentId);
        if ('pending' in fin && fin.pending) {
          result = await pollUntilComplete(prep.checkoutIntentId);
        } else {
          result = fin as StoreCheckoutResult;
        }
      } catch {
        // Finalize call lost (network) — the webhook will finalize; poll for it.
        result = await pollUntilComplete(prep.checkoutIntentId);
      }
      if (result) {
        onComplete(result);
      } else {
        // Charged, order creation still settling. Don't scare the customer —
        // the webhook guarantees the order; they'll get the email receipt.
        setPaidPending(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed — please try again');
    } finally {
      setBusy(false);
      setStage('idle');
    }
  }, [stripe, elements, busy, buildPayload, cardFunding, onComplete, pollUntilComplete]);

  const startPay = useCallback(() => {
    setError(null);
    if (rentals.length > 0 && !termsAccepted) return;
    void pay();
  }, [rentals.length, termsAccepted, pay]);

  if (paidPending) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-4">
        <p className="text-[14px] font-semibold text-green-900">Payment received — thank you!</p>
        <p className="text-[13px] text-green-800 mt-1">
          Your order is being confirmed. You'll receive an email confirmation from {businessName} shortly.
        </p>
      </div>
    );
  }

  const buttonLabel = busy
    ? stage === 'charging' ? 'Processing payment…' : stage === 'finalizing' ? 'Confirming your order…' : 'Checking your order…'
    : !needCardEntry
      ? 'Place order — paid from wallet'
      : chargeKind === 'full'
        ? `Pay now — ${money(buttonTotalCents)}`
        : `Place order — pay ${money(buttonTotalCents)} rental`;

  const termsOk = rentals.length === 0 || termsAccepted;
  const buttonEnabled = !disabled && !busy && termsOk && (!needCardEntry || cardComplete);

  return (
    <div>
      {/* Card entry — skipped entirely when the wallet covers the order */}
      {needCardEntry ? (
        <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-[17px] font-bold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-4.5 w-4.5 text-gray-500" style={{ width: 18, height: 18 }} />
            Payment
          </h2>
          <span className="flex items-center gap-1 text-[12px] text-gray-400">
            <Lock className="h-3 w-3" /> Secured by Stripe
          </span>
        </div>
        <div className="rounded-xl border border-gray-300 bg-white px-3.5 py-3.5 focus-within:ring-2 focus-within:ring-offset-0" style={{ ['--tw-ring-color' as string]: accent }}>
          <CardElement
            options={{
              hidePostalCode: false,
              style: {
                base: {
                  fontSize: '15px',
                  color: '#111827',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  '::placeholder': { color: '#9ca3af' },
                },
                invalid: { color: '#dc2626' },
              },
            }}
            onChange={(e) => {
              setCardComplete(e.complete);
              setCardError(e.error?.message || null);
              if (!e.complete) setCardFunding(null);
            }}
          />
        </div>
        {cardError && <p className="text-[12px] text-red-600 mt-1.5">{cardError}</p>}
        {surchargeApplies && estimatedSurchargeCents > 0 && (
          <p className="text-[12px] text-amber-700 mt-1.5">
            A {surcharge.percent}% card processing fee ({money(estimatedSurchargeCents)}) applies to credit cards — debit cards pay no fee.
          </p>
        )}
        {chargeKind === 'rental_split' && (
          <p className="text-[12px] text-gray-500 mt-1.5">
            Only your rental's first period is charged now — the rest of the order is invoiced after delivery.
          </p>
        )}
        </div>
      ) : (
        <div className="mb-4 rounded-xl border border-emerald-200 bg-emerald-50 px-3.5 py-3">
          <p className="text-[13px] font-medium text-emerald-800">
            Your wallet balance covers this order — no card needed.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 mb-3">
          <p className="text-[13px] text-red-700">{error}</p>
        </div>
      )}

      <button
        onClick={startPay}
        disabled={!buttonEnabled}
        className={`w-full py-4 rounded-lg text-base font-semibold transition-transform ${buttonEnabled ? 'text-white active:scale-[0.99]' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}
        style={buttonEnabled ? { backgroundColor: accent } : {}}
      >
        {buttonLabel}
      </button>
    </div>
  );
}
